import { Info, RefreshCcw, Save } from "lucide-react";
import { useEffect, useMemo } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { useTranslations } from "@/shared/lang/useTranslations";

import type { EndpointSaveOutcome } from "../../hooks/useOrganizationWebhookConfig";
import {
  ORGANIZATION_WEBHOOK_SECRET_BYTES,
  ORGANIZATION_WEBHOOK_SECRET_MAX_LENGTH,
  ORGANIZATION_WEBHOOK_SECRET_MIN_LENGTH,
  ORGANIZATION_WEBHOOK_SECRET_PATTERN,
  ORGANIZATION_WEBHOOK_SECRET_PREFIX,
} from "../../services/organizationWebhooksService";

export interface WebhookUrlDialogProps {
  open: boolean;
  /** The current URL when re-pointing an existing endpoint; undefined when registering the first one. */
  currentUrl?: string;
  /** Header the secret travels in, quoted in the field's helper text. */
  secretHeader: string;
  isSubmitting: boolean;
  onSubmit: (input: { url: string; secret?: string }) => Promise<EndpointSaveOutcome>;
  onClose: () => void;
}

const URL_MAX_LENGTH = 2048;
const FORM_ID = "webhook-endpoint-form";

/**
 * A secret in the same shape rotate-secret produces — `whsec_` plus base64url of 32 random bytes — so a
 * generated one is indistinguishable from one Coloca issued, whichever path created it. Mirrors
 * `generateOrganizationWebhookSecret()` in coloca-nest; `crypto.getRandomValues` is the browser's CSPRNG, the
 * counterpart of node's `randomBytes`.
 */
function generateSecret(): string {
  const bytes = new Uint8Array(ORGANIZATION_WEBHOOK_SECRET_BYTES);
  crypto.getRandomValues(bytes);
  const base64url = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  return `${ORGANIZATION_WEBHOOK_SECRET_PREFIX}${base64url}`;
}

/**
 * Where the endpoint is registered: the URL and the shared secret.
 *
 * The secret is the customer's to choose — most receivers already have one — and left blank Coloca generates
 * it. On a first registration, submitting also runs the handshake (save paused → ping → enable on 2xx), which
 * is why the button says "Register and verify" there and plainly "Save" on an edit.
 */
export function WebhookUrlDialog({
  open,
  currentUrl,
  secretHeader,
  isSubmitting,
  onSubmit,
  onClose,
}: WebhookUrlDialogProps) {
  const { t } = useTranslations("settings");
  const isFirstRegistration = currentUrl === undefined;

  const schema = useMemo(
    () =>
      z.object({
        url: z
          .string()
          .trim()
          .max(URL_MAX_LENGTH, { message: t("webhooks_url_invalid") })
          .url({ message: t("webhooks_url_invalid") })
          .refine((value) => value.toLowerCase().startsWith("https://"), {
            message: t("webhooks_url_invalid"),
          }),
        // Blank is a legitimate answer on both paths: create means "generate one", edit means "keep the
        // current one". So the bounds only apply once something is typed.
        secret: z
          .string()
          .trim()
          .refine(
            (value) =>
              value === "" ||
              (value.length >= ORGANIZATION_WEBHOOK_SECRET_MIN_LENGTH &&
                value.length <= ORGANIZATION_WEBHOOK_SECRET_MAX_LENGTH &&
                ORGANIZATION_WEBHOOK_SECRET_PATTERN.test(value)),
            { message: t("webhooks_secret_invalid", { min: ORGANIZATION_WEBHOOK_SECRET_MIN_LENGTH }) },
          ),
      }),
    [t],
  );
  const form = useZodForm(schema, { url: currentUrl ?? "", secret: "" });

  // Re-seed on every open so a cancelled edit never leaks its half-typed value — or, worse, its secret —
  // into the next one.
  useEffect(() => {
    if (open) form.reset({ url: currentUrl ?? "", secret: "" });
  }, [open, currentUrl, form]);

  const handleSubmit = async ({ url, secret }: z.output<typeof schema>) => {
    const outcome = await onSubmit({ url, secret: secret === "" ? undefined : secret });
    // A failed handshake still saved the endpoint: close and let the section show what the receiver said.
    if (outcome.saved) onClose();
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isSubmitting) onClose();
      }}
      title={
        isFirstRegistration ? t("webhooks_url_dialog_title_create") : t("webhooks_url_dialog_title_edit")
      }
      description={t("webhooks_url_dialog_desc")}
      size="default"
      footer={
        <>
          <ColocaDialogClose
            render={
              <ColocaButton variant="ghost" disabled={isSubmitting}>
                {t("webhooks_url_cancel")}
              </ColocaButton>
            }
          />
          <ColocaButton type="submit" form={FORM_ID} variant="primary" icon={Save} loading={isSubmitting}>
            {isFirstRegistration ? t("webhooks_url_register_and_verify") : t("webhooks_url_save")}
          </ColocaButton>
        </>
      }
    >
      <ColocaForm id={FORM_ID} form={form} onSubmit={handleSubmit} className="space-y-4">
        <ColocaInputField
          name="url"
          label={t("webhooks_url_field_label")}
          placeholder={t("webhooks_url_field_placeholder")}
          inputMode="url"
          autoComplete="off"
          maxLength={URL_MAX_LENGTH}
          disabled={isSubmitting}
        />

        <div className="space-y-1.5">
          <ColocaInputField
            name="secret"
            label={t("webhooks_secret_field_label")}
            description={
              isFirstRegistration
                ? t("webhooks_secret_field_hint_create", { header: secretHeader })
                : t("webhooks_secret_field_hint_edit", { header: secretHeader })
            }
            placeholder={t("webhooks_secret_field_placeholder")}
            autoComplete="off"
            maxLength={ORGANIZATION_WEBHOOK_SECRET_MAX_LENGTH}
            disabled={isSubmitting}
          />
          <ColocaButton
            type="button"
            variant="ghost"
            size="xs"
            icon={RefreshCcw}
            disabled={isSubmitting}
            onClick={() => form.setValue("secret", generateSecret(), { shouldValidate: true })}
          >
            {t("webhooks_secret_generate")}
          </ColocaButton>
        </div>

        {isFirstRegistration && (
          <ColocaAlert variant="secondary" icon={Info} text={t("webhooks_handshake_explainer")} />
        )}
      </ColocaForm>
    </ColocaDialog>
  );
}
